import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Upload, FileDown, RefreshCcw, AlertTriangle } from "lucide-react";
import Papa from "papaparse";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import { fetchCompanyCOA } from "../Accounting/FixedAssets/coaFetch";
import "../Accounting/FixedAssets/fixedAssets.css";

// ---- helpers ----
function safeStr(x) {
  return String(x ?? "").trim();
}
function safeLower(x) {
  return safeStr(x).toLowerCase();
}
function toNum(x) {
  const n = Number(
    String(x ?? "")
      .replace(/,/g, "")
      .trim()
  );
  return Number.isFinite(n) ? n : 0;
}

function downloadCsvTemplate() {
  const headers = [
    "sku",
    "name",
    "item_type", // stock|service
    "unit",
    "buffer_stock",
    "reorder_level",
    "sales_price", // ✅ NEW
    "opening_qty", // optional
    "unit_cost", // optional (required if opening_qty > 0 for stock)
  ];
  const rows = [
    ["SKU-001", "Rice 50kg", "stock", "bag", "5", "10", "45000", "20", "35000"],
    [
      "SERV-001",
      "Delivery service",
      "service",
      "job",
      "0",
      "0",
      "5000",
      "",
      "",
    ],
  ];
  const csv =
    headers.join(",") +
    "\n" +
    rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "inventory_items_import_template.csv";
  a.click();

  URL.revokeObjectURL(url);
}

// Normalize + validate row shape (keeps features + allows defaults)
function normalizeRow(r) {
  const sku = safeStr(r.sku);
  const name = safeStr(r.name);

  let item_type = safeLower(r.item_type) || "stock";
  if (!["stock", "service"].includes(item_type)) item_type = "stock";

  const unit = safeStr(r.unit) || "pcs";
  const buffer_stock = toNum(r.buffer_stock);
  const reorder_level = toNum(r.reorder_level);

  // ✅ NEW: sales_price
  const sales_price_raw = safeStr(r.sales_price);
  const sales_price = sales_price_raw ? toNum(sales_price_raw) : 0;

  const opening_qty_raw = safeStr(r.opening_qty);
  const unit_cost_raw = safeStr(r.unit_cost);

  const opening_qty = opening_qty_raw ? toNum(opening_qty_raw) : 0;
  const unit_cost = unit_cost_raw ? toNum(unit_cost_raw) : 0;

  return {
    sku,
    name,
    item_type,
    unit,
    buffer_stock,
    reorder_level,
    sales_price, // ✅ NEW
    opening_qty,
    unit_cost,
    __raw: r,
  };
}

export default function InventoryItemsImportModal({
  open,
  onClose,
  onImported,
}) {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  // parsed rows from CSV
  const [parsedRows, setParsedRows] = useState([]);

  // results list (per row)
  const [results, setResults] = useState([]); // { sku, status, message }

  // warehouses
  const [warehouses, setWarehouses] = useState([]);
  const [useDefaultWarehouse, setUseDefaultWarehouse] = useState(true);
  const [defaultWarehouseId, setDefaultWarehouseId] = useState("");

  // COA + default mapping
  const [coa, setCoa] = useState([]);
  const [useDefaultAccounts, setUseDefaultAccounts] = useState(true);
  const [defaultAccounts, setDefaultAccounts] = useState({
    inventory_account_id: "",
    cogs_account_id: "",
    adjustment_account_id: "",
  });

  // opening stock settings
  const [postOpeningStock, setPostOpeningStock] = useState(true);
  const [postToJournal, setPostToJournal] = useState(true);
  const [movementDate, setMovementDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [movementNotes, setMovementNotes] = useState("Opening stock import");

  // ---------- derived lists (hooks MUST NOT be conditional) ----------
  const assetAccounts = useMemo(
    () => (coa || []).filter((a) => a.type === "Asset"),
    [coa]
  );
  const cosAccounts = useMemo(
    () => (coa || []).filter((a) => a.type === "Cost of Sales"),
    [coa]
  );
  const adjAccounts = useMemo(
    () =>
      (coa || []).filter((a) => a.type === "Expense" || a.type === "Revenue"),
    [coa]
  );

  const normalizedRows = useMemo(() => {
    return (parsedRows || []).map(normalizeRow);
  }, [parsedRows]);

  const validation = useMemo(() => {
    const issues = [];
    const seen = new Set();

    normalizedRows.forEach((r, idx) => {
      const rowNo = idx + 1;

      if (!r.sku) issues.push({ rowNo, sku: r.sku, msg: "SKU is required" });
      if (!r.name) issues.push({ rowNo, sku: r.sku, msg: "Name is required" });

      const key = safeLower(r.sku);
      if (key) {
        if (seen.has(key))
          issues.push({ rowNo, sku: r.sku, msg: "Duplicate SKU in file" });
        seen.add(key);
      }

      // ✅ NEW: sales_price validation (optional but must be >= 0 if provided)
      if (r.sales_price < 0) {
        issues.push({
          rowNo,
          sku: r.sku,
          msg: "sales_price cannot be negative",
        });
      }

      if (r.item_type === "stock" && r.opening_qty > 0) {
        if (r.unit_cost < 0)
          issues.push({ rowNo, sku: r.sku, msg: "unit_cost must be >= 0" });
        if (Number.isNaN(r.unit_cost))
          issues.push({ rowNo, sku: r.sku, msg: "unit_cost invalid" });
      }
      if (r.opening_qty < 0)
        issues.push({
          rowNo,
          sku: r.sku,
          msg: "opening_qty cannot be negative",
        });
    });

    // default mapping validation (only if enabled)
    if (useDefaultAccounts) {
      if (!defaultAccounts.inventory_account_id)
        issues.push({
          rowNo: "-",
          sku: "-",
          msg: "Select default Inventory account",
        });
      if (!defaultAccounts.cogs_account_id)
        issues.push({
          rowNo: "-",
          sku: "-",
          msg: "Select default COGS account",
        });
      if (!defaultAccounts.adjustment_account_id)
        issues.push({
          rowNo: "-",
          sku: "-",
          msg: "Select default Adjustment account",
        });
    }

    // warehouse validation (only if enabled)
    if (useDefaultWarehouse && !defaultWarehouseId) {
      issues.push({ rowNo: "-", sku: "-", msg: "Select default Warehouse" });
    }

    return { issues, hasIssues: issues.length > 0 };
  }, [
    normalizedRows,
    useDefaultAccounts,
    defaultAccounts,
    useDefaultWarehouse,
    defaultWarehouseId,
  ]);

  const summary = useMemo(() => {
    const ok = results.filter((r) => r.status === "ok").length;
    const bad = results.filter((r) => r.status === "error").length;
    const warn = results.filter((r) => r.status === "warn").length;
    return { ok, bad, warn };
  }, [results]);

  const openingRowsCount = useMemo(() => {
    return normalizedRows.filter(
      (r) => r.item_type === "stock" && Number(r.opening_qty || 0) > 0
    ).length;
  }, [normalizedRows]);

  // ---------- data loaders ----------
  const loadWarehouses = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase
      .from("warehouses")
      .select("id,name,is_default")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (error) {
      console.error(error);
      return;
    }

    const list = data || [];
    setWarehouses(list);

    // set default if empty
    if (!defaultWarehouseId) {
      const def = list.find((w) => w.is_default) || list[0];
      if (def?.id) setDefaultWarehouseId(def.id);
    }
  }, [companyId, defaultWarehouseId]);

  const loadCOA = useCallback(async () => {
    if (!companyId) return;
    const data = await fetchCompanyCOA(companyId);
    setCoa(data || []);
  }, [companyId]);

  useEffect(() => {
    if (!open) return;
    setErr("");
    setNotice("");
    setResults([]);
    setParsedRows([]);

    // preload
    loadWarehouses();
    loadCOA();
  }, [open, loadWarehouses, loadCOA]);

  // ---------- file handlers ----------
  const handleFile = async (file) => {
    setErr("");
    setNotice("");
    setResults([]);

    if (!file) return;

    const ext = safeLower(file.name).split(".").pop();
    if (ext !== "csv") {
      setErr("Please upload a CSV file.");
      return;
    }

    setBusy(true);
    try {
      const text = await file.text();

      const parsed = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => safeLower(h),
      });

      if (parsed.errors?.length) {
        console.error(parsed.errors);
        setErr(parsed.errors[0]?.message || "CSV parse error.");
        setBusy(false);
        return;
      }

      const rows = parsed.data || [];
      setParsedRows(rows);

      if (!rows.length) setNotice("CSV loaded, but it contains no rows.");
    } catch (e) {
      setErr(e?.message || "Failed to read file.");
    } finally {
      setBusy(false);
    }
  };

  // ---------- import + opening stock ----------
  const runImport = async () => {
    if (!companyId) return;

    setErr("");
    setNotice("");
    setResults([]);

    if (!normalizedRows.length) {
      setErr("Upload a CSV first.");
      return;
    }

    if (validation.hasIssues) {
      setErr("Fix validation issues before importing.");
      return;
    }

    setBusy(true);
    try {
      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (authErr || !user) {
        setErr("Not authenticated.");
        setBusy(false);
        return;
      }

      // 1) Fetch existing SKUs to avoid duplicate insert errors
      const skus = normalizedRows.map((r) => r.sku).filter(Boolean);
      const { data: existing, error: existErr } = await supabase
        .from("inventory_items")
        .select("id,sku")
        .eq("company_id", companyId)
        .in("sku", skus);

      if (existErr) throw existErr;

      const existingBySku = new Map(
        (existing || []).map((x) => [safeLower(x.sku), x])
      );

      const createdItems = []; // { sku, id }
      const rowResults = [];

      // 2) Upsert items one-by-one (keeps behaviour stable + better per-row error messages)
      for (const r of normalizedRows) {
        try {
          const key = safeLower(r.sku);
          const found = existingBySku.get(key);

          const payload = {
            company_id: companyId,
            sku: r.sku,
            name: r.name,
            item_type: r.item_type,
            unit: r.unit || "pcs",
            is_active: true,
            buffer_stock: Number(r.buffer_stock || 0),
            reorder_level: Number(r.reorder_level || 0),

            // ✅ NEW: persist sales_price
            sales_price: Number(r.sales_price || 0),

            inventory_account_id:
              r.item_type === "stock" && useDefaultAccounts
                ? defaultAccounts.inventory_account_id
                : null,
            cogs_account_id:
              r.item_type === "stock" && useDefaultAccounts
                ? defaultAccounts.cogs_account_id
                : null,
            adjustment_account_id:
              r.item_type === "stock" && useDefaultAccounts
                ? defaultAccounts.adjustment_account_id
                : null,
          };

          if (found?.id) {
            // Update existing item (keeps system tolerant)
            const { error: upErr } = await supabase
              .from("inventory_items")
              .update(payload)
              .eq("company_id", companyId)
              .eq("id", found.id);

            if (upErr) throw upErr;

            createdItems.push({ sku: r.sku, id: found.id });
            rowResults.push({
              sku: r.sku,
              status: "ok",
              message: "Updated item",
            });
          } else {
            const { data: ins, error: insErr } = await supabase
              .from("inventory_items")
              .insert(payload)
              .select("id,sku")
              .single();

            if (insErr) throw insErr;

            createdItems.push({ sku: r.sku, id: ins.id });
            rowResults.push({
              sku: r.sku,
              status: "ok",
              message: "Created item",
            });
          }
        } catch (e) {
          rowResults.push({
            sku: r.sku || "—",
            status: "error",
            message: e?.message || "Failed to import item",
          });
        }
      }

      // 3) Opening stock post (optional)
      if (postOpeningStock && openingRowsCount > 0) {
        if (!useDefaultWarehouse || !defaultWarehouseId) {
          rowResults.push({
            sku: "—",
            status: "error",
            message: "Default warehouse is required to post opening stock.",
          });
        } else {
          // Build movement lines for ONLY stock items with opening_qty > 0
          const itemIdBySku = new Map(
            createdItems.map((x) => [safeLower(x.sku), x.id])
          );
          const lines = [];

          for (const r of normalizedRows) {
            if (r.item_type !== "stock") continue;
            if (!(Number(r.opening_qty || 0) > 0)) continue;

            const itemId = itemIdBySku.get(safeLower(r.sku));
            if (!itemId) {
              rowResults.push({
                sku: r.sku,
                status: "error",
                message: "Opening stock skipped: item not found after import.",
              });
              continue;
            }

            // Your SQL requires unit_cost >= 0 for IN, and line requires item_id, quantity, unit_cost
            lines.push({
              item_id: itemId,
              quantity: Number(r.opening_qty || 0),
              unit_cost: Number(r.unit_cost || 0),
              batch_no: null,
              expiry_date: null,
              serials: [],
            });
          }

          if (lines.length) {
            const { error: rpcErr } = await supabase.rpc(
              "post_inventory_movement",
              {
                // ✅ MUST MATCH YOUR FUNCTION SIGNATURE EXACTLY
                p_company_id: companyId,
                p_created_by: user.id,
                p_movement_type: "IN",
                p_movement_date: movementDate,
                p_warehouse_id: defaultWarehouseId,
                p_warehouse_to_id: null,
                p_reference_type: "opening_stock_import",
                p_reference_id: null,
                p_notes: movementNotes || "Opening stock import",
                p_lines: lines,
                p_post_to_journal: Boolean(postToJournal),
              }
            );

            if (rpcErr) {
              // make it clear and per import, not crashing everything
              rowResults.push({
                sku: "—",
                status: "error",
                message: rpcErr.message || "Opening stock post failed",
              });
            } else {
              rowResults.push({
                sku: "—",
                status: "ok",
                message: `Opening stock posted (${lines.length} lines)`,
              });
            }
          }
        }
      }

      setResults(rowResults);
      onImported?.();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  // ✅ Early return AFTER hooks
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.97)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="fa-panel"
        style={{
          width: "min(980px, 100%)",
          maxHeight: "90vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>
              Upload Inventory Items (CSV)
            </div>
            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
              Upload a CSV to bulk create items. Optionally post opening stock
              as a Stock IN movement.
            </div>
          </div>
          <button
            className="fa-icon-btn"
            onClick={onClose}
            title="Close"
            disabled={busy}
          >
            <X size={16} />
          </button>
        </div>

        {/* Toolbar */}
        <div
          className="fa-toolbar"
          style={{ marginTop: 12, gap: 10, flexWrap: "wrap" }}
        >
          <button
            className="fa-btn"
            onClick={downloadCsvTemplate}
            disabled={busy}
          >
            <FileDown size={16} /> Download template
          </button>

          <label
            className="fa-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
          >
            <Upload size={16} /> Choose CSV
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
              disabled={busy}
            />
          </label>

          <button
            className="fa-btn"
            onClick={() => {
              setParsedRows([]);
              setResults([]);
              setErr("");
              setNotice("");
            }}
            disabled={busy}
          >
            <RefreshCcw size={16} /> Clear
          </button>

          <div style={{ marginLeft: "auto", opacity: 0.85, fontSize: 13 }}>
            Rows loaded: <b>{normalizedRows.length}</b>
            {openingRowsCount > 0 && (
              <>
                {" "}
                • Opening stock rows: <b>{openingRowsCount}</b>
              </>
            )}
          </div>
        </div>

        {/* Defaults / Settings (✅ kept) */}
        <div className="fa-card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>
            Import Defaults
          </div>

          {/* Default warehouse */}
          <div style={{ display: "grid", gap: 8 }}>
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                fontSize: 13,
              }}
            >
              <input
                type="checkbox"
                checked={useDefaultWarehouse}
                onChange={(e) => setUseDefaultWarehouse(e.target.checked)}
                disabled={busy}
              />
              Use default warehouse for opening stock posting
            </label>

            {useDefaultWarehouse && (
              <select
                className="fa-select"
                value={defaultWarehouseId}
                onChange={(e) => setDefaultWarehouseId(e.target.value)}
                disabled={busy}
              >
                <option value="">Select warehouse…</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                    {w.is_default ? " (Default)" : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div style={{ height: 12 }} />

          {/* Default accounts */}
          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={useDefaultAccounts}
              onChange={(e) => setUseDefaultAccounts(e.target.checked)}
              disabled={busy}
            />
            Apply default accounts mapping to stock items (Inventory / COGS /
            Adjustment)
          </label>

          {useDefaultAccounts && (
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <div>
                <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                  Inventory Asset Account
                </div>
                <select
                  className="fa-select"
                  value={defaultAccounts.inventory_account_id}
                  onChange={(e) =>
                    setDefaultAccounts((p) => ({
                      ...p,
                      inventory_account_id: e.target.value,
                    }))
                  }
                  disabled={busy}
                >
                  <option value="">Select account…</option>
                  {assetAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code ? `${a.code} - ` : ""}
                      {a.name} ({a.category || "—"})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                  COGS Account
                </div>
                <select
                  className="fa-select"
                  value={defaultAccounts.cogs_account_id}
                  onChange={(e) =>
                    setDefaultAccounts((p) => ({
                      ...p,
                      cogs_account_id: e.target.value,
                    }))
                  }
                  disabled={busy}
                >
                  <option value="">Select account…</option>
                  {cosAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code ? `${a.code} - ` : ""}
                      {a.name} ({a.category || "—"})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                  Adjustment / Clearing Account
                </div>
                <select
                  className="fa-select"
                  value={defaultAccounts.adjustment_account_id}
                  onChange={(e) =>
                    setDefaultAccounts((p) => ({
                      ...p,
                      adjustment_account_id: e.target.value,
                    }))
                  }
                  disabled={busy}
                >
                  <option value="">Select account…</option>
                  {adjAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code ? `${a.code} - ` : ""}
                      {a.name} ({a.type})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Opening stock options (✅ kept) */}
        <div className="fa-card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>
            Opening Stock Posting
          </div>

          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={postOpeningStock}
              onChange={(e) => setPostOpeningStock(e.target.checked)}
              disabled={busy}
            />
            Auto-post opening stock (Stock IN) using post_inventory_movement
          </label>

          {postOpeningStock && (
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                <label className="stack">
                  Movement Date
                  <input
                    className="fa-input"
                    type="date"
                    value={movementDate}
                    onChange={(e) => setMovementDate(e.target.value)}
                    disabled={busy}
                  />
                </label>

                <label className="stack">
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <input
                      type="checkbox"
                      checked={postToJournal}
                      onChange={(e) => setPostToJournal(e.target.checked)}
                      disabled={busy}
                    />
                    Post to Journal
                  </span>
                  <span style={{ fontSize: 12, opacity: 0.75 }}>
                    Uses each item’s Inventory + Adjustment mapping.
                  </span>
                </label>
              </div>

              <label className="stack">
                Notes
                <input
                  className="fa-input"
                  value={movementNotes}
                  onChange={(e) => setMovementNotes(e.target.value)}
                  disabled={busy}
                  placeholder="Opening stock import"
                />
              </label>
            </div>
          )}

          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 8 }}>
            CSV columns used for opening stock: <b>opening_qty</b>,{" "}
            <b>unit_cost</b> (stock items only). Warehouse is selected here, not
            in the CSV.
          </div>

          {/* ✅ NEW hint */}
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
            CSV column for selling price: <b>sales_price</b> (optional; defaults
            to 0 if blank).
          </div>
        </div>

        {/* Errors / notices */}
        {err && (
          <div className="banner error" style={{ marginTop: 12 }}>
            {err}
          </div>
        )}
        {notice && (
          <div className="banner" style={{ marginTop: 12 }}>
            {notice}
          </div>
        )}

        {/* Validation */}
        {validation.issues.length > 0 && (
          <div
            className="fa-card"
            style={{
              marginTop: 12,
              border: "1px solid rgba(255,180,180,0.35)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontWeight: 900,
              }}
            >
              <AlertTriangle size={16} /> Validation issues (
              {validation.issues.length})
            </div>
            <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
              Fix these before importing:
            </div>
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              {validation.issues.slice(0, 12).map((x, i) => (
                <li key={i} style={{ fontSize: 13, opacity: 0.9 }}>
                  <b>Row {x.rowNo}</b>{" "}
                  {x.sku && x.sku !== "-" ? `(${x.sku})` : ""}: {x.msg}
                </li>
              ))}
              {validation.issues.length > 12 && (
                <li style={{ fontSize: 13, opacity: 0.9 }}>
                  …and {validation.issues.length - 12} more.
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Preview table */}
        <div className="fa-panel" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>Preview</div>

          <table className="fa-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Name</th>
                <th>Type</th>
                <th>Unit</th>
                <th>Buffer</th>
                <th>Reorder</th>
                <th>Sales Price</th> {/* ✅ NEW */}
                <th>Opening Qty</th>
                <th>Unit Cost</th>
              </tr>
            </thead>
            <tbody>
              {normalizedRows.slice(0, 30).map((r, idx) => (
                <tr key={idx}>
                  <td>{r.sku || "—"}</td>
                  <td style={{ fontWeight: 900 }}>{r.name || "—"}</td>
                  <td>{r.item_type}</td>
                  <td>{r.unit}</td>
                  <td>{r.buffer_stock}</td>
                  <td>{r.reorder_level}</td>
                  <td>{r.sales_price || 0}</td> {/* ✅ NEW */}
                  <td>{r.opening_qty || 0}</td>
                  <td>{r.unit_cost || 0}</td>
                </tr>
              ))}
              {normalizedRows.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ opacity: 0.75 }}>
                    No rows loaded yet.
                  </td>
                </tr>
              )}
              {normalizedRows.length > 30 && (
                <tr>
                  <td colSpan={9} style={{ opacity: 0.75 }}>
                    Showing first 30 rows of {normalizedRows.length}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="fa-card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 900 }}>
              Results — OK: {summary.ok}, Warnings: {summary.warn}, Errors:{" "}
              {summary.bad}
            </div>
            <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
              {results.slice(0, 50).map((r, i) => (
                <div key={i} style={{ fontSize: 13, opacity: 0.95 }}>
                  <b style={{ textTransform: "uppercase" }}>
                    {r.status === "ok"
                      ? "OK"
                      : r.status === "warn"
                      ? "WARN"
                      : "ERROR"}
                  </b>{" "}
                  — <b>{r.sku}</b>: {r.message}
                </div>
              ))}
              {results.length > 50 && (
                <div style={{ fontSize: 13, opacity: 0.85 }}>
                  Showing first 50 of {results.length} results…
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div
          className="fa-actions"
          style={{ marginTop: 12, justifyContent: "flex-end" }}
        >
          <button className="fa-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={runImport}
            disabled={busy || normalizedRows.length === 0}
            title={validation.hasIssues ? "Fix validation issues first" : ""}
          >
            {busy ? "Importing…" : "Import Items"}
          </button>
        </div>
      </div>
    </div>
  );
}
